import { useState, useEffect, useMemo } from 'react';
import { NetworkLegend } from './NetworkLegend';
import { useTranslation } from 'react-i18next';
import { FilterSelectField, Option, SearchField } from '@/components/Form';
import { graphql, useQuery } from '@/lib/graphql';
import { Core } from 'cytoscape';
import { SingleValue } from 'react-select';
import { CollapsibleTagList } from '@/components/Elements';

import { TopicTagWithFilterTooltip } from '../tooltips/TopicTagWithFilterTooltip';
import { TagContainer } from '@/features/analyze/components/Tag';
import { NetworkData, NodeType } from '@/features/connect-dots/types/cytoscape';
import { useNodeSearch } from '@/features/connect-dots/hooks/useNodeSearch';

import { SearchHelpIconModal } from '@/features/analyze/components/SearchHelpIconModal';

type ToolbarProps = {
  isOpen: boolean;
  cytoscapeCoreRefObject: Core | null;
  filteredData: NetworkData | null;
  nodeTypeFilters: { [key in NodeType]: boolean };
  updateNodeTypeFilter: (nodeType: NodeType, isVisible: boolean) => void;
};

type ThemeOption = Option<string>;

const getTopicTagsQuery = graphql(`
  query GetTopicTags(
    $condition: TopicTagSearchConditionInput!
    $page: Int = 1
    $perPage: Int = 9999
  ) {
    topicTags(condition: $condition, page: $page, perPage: $perPage) {
      items {
        name
      }
      pageInfo {
        currentPage
        hasNextPage
        totalCount
      }
    }
  }
`);

export function Toolbar({
  isOpen,
  cytoscapeCoreRefObject,
  filteredData,
  nodeTypeFilters,
  updateNodeTypeFilter,
}: ToolbarProps) {
  const { t } = useTranslation();
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(
    undefined
  );
  const [queryInput, setQueryInput] = useState<string>('');
  const [searchedQuery, setSearchedQuery] = useState<string>('');

  const themeOptions = useMemo<ThemeOption[]>(() => {
    return (filteredData?.nodes ?? [])
      .filter((n) => n.nodeType === NodeType.THEME)
      .map((n) => ({
        value: n.id,
        label: n.label ?? t('未設定'),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredData?.nodes, t]);

  const selectedThemeOption = useMemo(
    () =>
      themeOptions.find((option) => option.value === selectedThemeId) ?? null,
    [selectedThemeId, themeOptions]
  );

  const {
    searchedNodes,
    setSearchedNodes,
    isDropdownOpen,
    setIsDropdownOpen,
    highlightSearchResults,
    highlightNodeAndConnections,
    removeHighlighting,
    clearSearchResults,
    resetSearchState,
  } = useNodeSearch();

  // GraphQL クエリを実行
  const { data, error } = useQuery(getTopicTagsQuery, {
    variables: {
      condition: { query: searchedQuery },
      page: 1,
      perPage: 9999,
    },
    skip: !searchedQuery, // 検索ワードが空の場合はクエリをスキップ
  });

  // クエリ結果の処理
  useEffect(() => {
    if (!searchedQuery) {
      return;
    }

    if (error) {
      console.error('Error fetching topic tags:', error);
      return;
    }

    if (data && cytoscapeCoreRefObject) {
      const matchedNodes = highlightSearchResults(
        cytoscapeCoreRefObject,
        data.topicTags.items.map((item: { name: string }) => item.name)
      );
      setSearchedNodes(matchedNodes);
      setIsDropdownOpen(true);
    }
  }, [
    data,
    error,
    searchedQuery,
    cytoscapeCoreRefObject,
    highlightSearchResults,
    setSearchedNodes,
    setIsDropdownOpen,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setQueryInput('');
      setSearchedQuery('');

      if (cytoscapeCoreRefObject) {
        clearSearchResults(cytoscapeCoreRefObject);
        return;
      }

      resetSearchState();
    }
  }, [isOpen, cytoscapeCoreRefObject, clearSearchResults, resetSearchState]);

  if (!cytoscapeCoreRefObject) {
    return null; // Cytoscapeが初期化されていない場合は何も表示しない
  }

  const clearThemeSelection = () => {
    setSelectedThemeId(undefined);
    removeHighlighting(cytoscapeCoreRefObject);
  };

  const handleThemeChange = (selectedOption: SingleValue<ThemeOption>) => {
    const nextThemeId = selectedOption?.value;

    if (!cytoscapeCoreRefObject) return;

    if (!nextThemeId) {
      clearThemeSelection();
      return;
    }

    setSelectedThemeId(nextThemeId);

    const target = cytoscapeCoreRefObject.getElementById(nextThemeId);
    if (target && target.length > 0) {
      cytoscapeCoreRefObject.animate({
        center: { eles: target },
        duration: 600,
        easing: 'ease-in-out',
        zoom: 0.8,
      });
      removeHighlighting(cytoscapeCoreRefObject);
      highlightNodeAndConnections(cytoscapeCoreRefObject, target.id());
    }
  };

  return (
    <>
      <div className="relative m-2">
        <div className="flex justify-between gap-4">
          <div className="relative flex-1">
            {/* 検索バー */}
            <div className="min-w-0">
              <SearchField
                value={queryInput}
                onValueChange={setQueryInput}
                onSearch={(submittedQuery) => {
                  if (!submittedQuery) {
                    clearSearchResults(cytoscapeCoreRefObject);
                  }

                  setSearchedQuery(submittedQuery ?? '');
                }}
                leftIcon={<SearchHelpIconModal />}
              />
            </div>
            {/* タグリスト表示部分 */}
            {isDropdownOpen && (
              <div className="relative">
                <div className="absolute left-0 top-full z-20 mt-2 max-h-64 overflow-y-auto rounded border bg-white px-4 pb-4 pt-2 shadow-md">
                  <div className="pb-2 text-sm text-gray-600">
                    <span>{t('もしかして・・・')}</span>
                  </div>
                  {searchedNodes.length === 0 ? (
                    <div className="py-2 text-sm text-gray-500">
                      {t('該当するタグが見つかりません')}
                    </div>
                  ) : (
                    <TagContainer>
                      <CollapsibleTagList
                        items={searchedNodes}
                        maxVisibleItems={12}
                        textColor="text-rw-black"
                        renderTag={(item) => (
                          <TopicTagWithFilterTooltip
                            key={item.id}
                            cytoscapeCoreRefObject={cytoscapeCoreRefObject}
                            content={item}
                          />
                        )}
                      />
                    </TagContainer>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0">
            <div className="flex items-center gap-2">
              {/* 表示設定メニュー */}
              <NetworkLegend
                nodeCount={filteredData?.nodes.length ?? 0}
                nodeTypeFilters={nodeTypeFilters}
                updateNodeTypeFilter={updateNodeTypeFilter}
              />

              {/* テーマ移動 */}
              <div className="flex h-10 items-stretch overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm">
                <label htmlFor="theme-focus-select" className="sr-only">
                  {t('テーマへ移動...')}
                </label>
                <FilterSelectField<ThemeOption, false>
                  inputId="theme-focus-select"
                  className="w-36 text-sm"
                  value={selectedThemeOption}
                  options={themeOptions}
                  onChange={handleThemeChange}
                  placeholder={t('テーマへ移動...')}
                  isClearable={true}
                  isSearchable={true}
                  styleProps={{ size: 'lg' }}
                  menuPosition="fixed"
                  aria-label={t('テーマへ移動...')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
